import {
  Box,
  DateInput,
  DateInputs,
  Form,
  fromInputsToIsoDate,
  fromIsoDateToInputs,
  Heading,
  mdBumps,
  PrimaryButton,
  TextInput,
  useModal,
  xlBumps,
} from '@wrisk/ui-components'
import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useAsyncCallback } from 'react-async-hook'
import { generatePath, useNavigate, useSearchParams } from 'react-router'

import { SessionFormRequest } from '../../clients/anonymous'
import {
  tDefaults,
  TKey,
  useWriskTranslation,
} from '../../infrastructure/internationalisation'
import { getFormats, useConfig } from '../../state/configuration'
import { isAxiosError } from '../../util/axios'
import { AnonymousPrincipal } from '../authentication'
import { WriskTrans } from '../organisms/trans'
import { FullPage } from '../templates'
import { RetrievePageError } from './RetrievePageError'
import { RetrievePath } from './RetrievePath'

const tKey = TKey('pages.retrieve')

export interface RetrievePageProps {
  tName?: string
  principal: AnonymousPrincipal
}

export const RetrievePage: FunctionComponent<RetrievePageProps> = ({
  tName = 'saveMyQuote',
  principal,
}) => {
  const { t } = useWriskTranslation()
  const [params] = useSearchParams()

  const navigate = useNavigate()

  const modal = useModal()

  const {
    postalCode: { regex },
  } = useConfig(getFormats)

  const ref = params.get('ref') ?? undefined

  const datePlaceholders = useMemo(
    () => ({
      day: t(tDefaults('inputs.date.placeholders.day')),
      month: t(tDefaults('inputs.date.placeholders.month')),
      year: t(tDefaults('inputs.date.placeholders.year')),
    }),
    [t],
  )

  const mapExceptionToMessage = (error: unknown): string =>
    isAxiosError(error) && error.response?.status === 401
      ? error.response.status.toString()
      : 'unknown'

  const onSubmit = useAsyncCallback(async () => {
    if (!isTokenRequest(tokenRequest)) {
      return
    }

    try {
      await principal.update(tokenRequest)
      navigate(generatePath(RetrievePath.RETRIEVE_PROPOSAL, tokenRequest))
    } catch (e) {
      const messageKey = mapExceptionToMessage(e)

      const title = (
        <WriskTrans t={t} i18nKey={tKey(tName, `errors.${messageKey}.header`)} />
      )
      const message = (
        <WriskTrans t={t} i18nKey={tKey(tName, `errors.${messageKey}.message`)} />
      )

      modal.show({
        content: <RetrievePageError title={title} message={message} />,
      })
    }
  })

  const [tokenRequest, setTokenRequest] = useState<Partial<SessionFormRequest>>({
    externalReference: ref,
  })

  const onExternalReferenceChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setTokenRequest((old) => ({ ...old, externalReference: target.value }))
    },
    [setTokenRequest],
  )

  const onDateOfBirthChange = useCallback(
    (inputs: DateInputs) => {
      setTokenRequest((old) => ({ ...old, dateOfBirth: fromInputsToIsoDate(inputs) }))
    },
    [setTokenRequest],
  )

  const onPostalCodeChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setTokenRequest((old) => ({
        ...old,
        postalCode: target.value.toUpperCase(),
      }))
    },
    [setTokenRequest],
  )

  const isTokenRequest = useCallback(
    (request: Partial<SessionFormRequest>): request is SessionFormRequest => {
      return Boolean(
        request.externalReference &&
          request.postalCode &&
          isValidPostalCode(request.postalCode, regex) &&
          request.dateOfBirth,
      )
    },
    [regex],
  )

  return (
    <FullPage
      pageId='retrieve-page'
      header={t(tKey(tName, 'header'))}
      subheader={t(tKey(tName, 'subheader'))}
    >
      <Form onSubmit={onSubmit.execute} data-testid='retrieve-quote'>
        {!ref && (
          <Box width={1} mb={xlBumps} data-testid='external-reference'>
            <Heading variant='h3' typoSize='md' pb={mdBumps}>
              {t(tKey(tName, 'form.external-reference.header'))}
            </Heading>
            <TextInput
              width={[1, 1, 4 / 5]}
              value={tokenRequest.externalReference ?? ''}
              onChange={onExternalReferenceChange}
              placeholder={t<string>(tKey(tName, 'form.external-reference.placeholder'))}
            />
          </Box>
        )}

        <Box width={1} mb={xlBumps} data-testid='date-of-birth'>
          <Heading variant='h3' typoSize='md' pb={mdBumps}>
            {t(tKey(tName, 'form.date-of-birth.header'))}
          </Heading>
          <DateInput
            dateInputs={fromIsoDateToInputs(tokenRequest.dateOfBirth)}
            onChange={onDateOfBirthChange}
            placeholders={datePlaceholders}
          />
        </Box>

        <Box width={1} mb={xlBumps} data-testid='postal-code'>
          <Heading variant='h3' typoSize='md' pb={mdBumps}>
            {t(tKey(tName, 'form.postal-code.header'))}
          </Heading>
          <TextInput
            value={tokenRequest.postalCode ?? ''}
            onChange={onPostalCodeChange}
            placeholder={t<string>(tKey(tName, 'form.postal-code.placeholder'))}
          />
        </Box>

        <PrimaryButton
          layout='fixed'
          type='submit'
          loading={onSubmit.loading}
          disabled={!isTokenRequest(tokenRequest)}
        >
          {t(tKey(tName, 'form.submit'))}
        </PrimaryButton>
      </Form>
    </FullPage>
  )
}

const isValidPostalCode = (input: string, regex?: string): boolean =>
  regex ? new RegExp(regex).test(input) : true
